<template>
    <div class="card mb-4">
      <div class="card-header pb-3">
        <div class="row g-3 align-items-center">
          <div class="col-12 col-md-5">
            <h5 class="mb-2 text-info">Participantes Recreativo Juvenil</h5>
            <span class="badge bg-gradient-info">
              <i class="fa fa-users me-1" aria-hidden="true"></i>{{ youths.total || 0 }} inscritos
            </span>
          </div>
          <div class="col-12 col-md-7">
            <div class="d-flex flex-wrap justify-content-md-end align-items-center gap-2">
              <input
                id="search-youths"
                v-model="search"
                @input="searchYouths"
                type="text"
                class="form-control"
                style="max-width: 240px;"
                placeholder="Buscar por nombre o matrícula"
                aria-label="Buscar juvenil por nombre o matrícula"
              />
              <label class="btn bg-gradient-info mb-0">
                <i class="fa fa-file-import me-1" aria-hidden="true"></i> Importar Juveniles
                <input type="file" ref="file" name="file" @change="onChange" hidden>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body py-2 border-bottom">
        <div class="row g-2 align-items-end">
          <div class="col-6 col-md-auto">
            <label for="flt-continent" class="form-label text-xs text-secondary mb-1">Continente</label>
            <select id="flt-continent" v-model="filterContinent" class="form-select form-select-sm" @change="onFilterChange">
              <option value="">Todos</option>
              <option value="América">América</option>
              <option value="Europa">Europa</option>
              <option value="África">África</option>
            </select>
          </div>
          <div class="col-6 col-md-auto">
            <label for="flt-country" class="form-label text-xs text-secondary mb-1">País (equipo)</label>
            <select id="flt-country" v-model="filterTeam" class="form-select form-select-sm" @change="resetAndFetch">
              <option value="">Todos</option>
              <option v-for="t in filteredCountries" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div v-if="filterContinent || filterTeam" class="col-12 col-md-auto">
            <button class="btn btn-sm btn-outline-secondary mb-0" @click="clearFilters">
              <i class="fas fa-times me-1" aria-hidden="true"></i> Limpiar
            </button>
          </div>
        </div>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div class="table-responsive p-0">
          <table class="table table-hover zebra align-items-center mb-0">
            <thead>
              <tr>
                <th class="sortable text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4" @click="setSort('name')">
                  Nombre <i class="fas ms-1" :class="sortIcon('name')" aria-hidden="true"></i>
                </th>
                <th class="sortable text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" @click="setSort('matricula')">
                  Matrícula <i class="fas ms-1" :class="sortIcon('matricula')" aria-hidden="true"></i>
                </th>
                <th class="sortable text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" @click="setSort('age')">
                  Edad <i class="fas ms-1" :class="sortIcon('age')" aria-hidden="true"></i>
                </th>
                <th class="sortable text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" @click="setSort('continent')">
                  Continente <i class="fas ms-1" :class="sortIcon('continent')" aria-hidden="true"></i>
                </th>
                <th class="sortable text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" @click="setSort('country')">
                  País <i class="fas ms-1" :class="sortIcon('country')" aria-hidden="true"></i>
                </th>
                <th class="text-center text-secondary text-xxs font-weight-bolder opacity-7">Ver</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr v-for="n in 8" :key="'sk' + n">
                  <td class="ps-4"><div class="skeleton" style="width: 70%"></div></td>
                  <td><div class="skeleton" style="width: 50%"></div></td>
                  <td><div class="skeleton" style="width: 40%"></div></td>
                  <td><div class="skeleton mx-auto" style="width: 60%"></div></td>
                  <td><div class="skeleton mx-auto" style="width: 60%"></div></td>
                  <td><div class="skeleton mx-auto" style="width: 30%"></div></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="youth in youths.data" :key="youth.id" class="cursor-pointer" @click="openRecord(youth.id)">
                  <td class="ps-4">
                    <h6 class="mb-0 text-sm">{{ youth.first_name }} {{ youth.last_name }}</h6>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">{{ youth.customer?.matricula || '—' }}</p>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">{{ youth.age }}</p>
                  </td>
                  <td class="align-middle text-center">
                    <p class="text-xs font-weight-bold mb-0">{{ youth.teams[0]?.continent || '—' }}</p>
                  </td>
                  <td class="align-middle text-center">
                    <p class="text-xs font-weight-bold mb-0">{{ youth.teams[0]?.name || 'No asignado' }}</p>
                  </td>
                  <td class="align-middle text-center">
                    <button class="btn btn-link text-info mb-0 p-1" aria-label="Ver juvenil" @click.stop="openRecord(youth.id)">
                      <i class="fas fa-eye" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- Empty state -->
          <div v-if="!isLoading && !youths.data.length" class="empty-state text-center">
            <div class="empty-icon mx-auto mb-3">
              <i class="fas fa-user-graduate" aria-hidden="true"></i>
            </div>
            <h6 class="text-dark mb-1">Sin juveniles</h6>
            <p class="text-sm text-secondary mb-0">
              {{ (search || filterContinent || filterTeam) ? 'No encontramos resultados para los filtros aplicados.' : 'Aún no hay juveniles inscritos. Importa el Excel para comenzar.' }}
            </p>
          </div>
        </div>
        <div v-if="!isLoading && youths.data.length" class="d-flex justify-content-end align-items-center px-4 py-3">
          <div class="d-flex align-items-center">
            <label class="me-2">Mostrar:</label>
            <select v-model="perPage" @change="fetchYouths" class="form-select me-3">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <button @click="changePage('first')" :disabled="!youths.prev_page_url" class="btn btn-primary btn-sm me-2">Primera</button>
          <button @click="changePage('prev')" :disabled="!youths.prev_page_url" class="btn btn-primary btn-sm me-2">Anterior</button>
          <span>Página {{ youths.current_page }} de {{ youths.last_page }}</span>
          <button @click="changePage('next')" :disabled="!youths.next_page_url" class="btn btn-primary btn-sm me-2">Siguiente</button>
          <button @click="changePage('last')" :disabled="youths.current_page === youths.last_page" class="btn btn-primary btn-sm">Última</button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from "axios";
  const PUBLIC_API_URL = process.env.VUE_APP_PUBLIC_API_URL;
  export default {
    name: "juvenil-table",
    data() {
      return {
        isLoading: false,
        youths: { data: [] },
        search: '',
        currentPage: 1,
        perPage: 20,
        sortBy: 'name',
        sortDir: 'asc',
        countries: [],
        filterContinent: '',
        filterTeam: '',
      };
    },
    computed: {
      filteredCountries() {
        if (!this.filterContinent) return this.countries;
        return this.countries.filter(t => t.continent === this.filterContinent);
      },
    },
    created() {
      this.fetchYouths();
      this.fetchCountries();
    },
    methods: {
      fetchCountries() {
        axios.get(`${PUBLIC_API_URL}/teams-youths`)
          .then(response => { this.countries = response.data; })
          .catch(() => { this.countries = []; });
      },
      onFilterChange() {
        // Al cambiar continente, limpiar pais si no pertenece.
        if (this.filterTeam && this.filterContinent) {
          const stillValid = this.filteredCountries.some(t => t.id === this.filterTeam);
          if (!stillValid) this.filterTeam = '';
        }
        this.resetAndFetch();
      },
      clearFilters() {
        this.filterContinent = '';
        this.filterTeam = '';
        this.resetAndFetch();
      },
      resetAndFetch() {
        this.currentPage = 1;
        this.fetchYouths();
      },
      fetchYouths() {
        this.isLoading = true;
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          search: this.search,
          sort_by: this.sortBy,
          sort_dir: this.sortDir,
          continent: this.filterContinent || undefined,
          team_id: this.filterTeam || undefined,
        };
        axios.get(`${PUBLIC_API_URL}/youths`, { params })
          .then(response => {
            this.youths = response.data;
          })
          .catch(error => {
            console.error("There was an error fetching the youths!", error);
            this.youths = { data: [] };
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
      changePage(direction) {
        if (direction === 'next' && this.youths.next_page_url) {
          this.currentPage++;
        } else if (direction === 'prev' && this.youths.prev_page_url) {
          this.currentPage--;
        } else if (direction === 'first') {
          this.currentPage = 1;
        } else if (direction === 'last') {
          this.currentPage = this.youths.last_page;
        }
        this.fetchYouths();
      },
      searchYouths() {
        this.currentPage = 1;
        this.fetchYouths();
      },
      openRecord(id) {
        this.$router.push(`/recreativo-juvenil/${id}`);
      },
      setSort(column) {
        if (this.sortBy === column) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortBy = column;
          this.sortDir = 'asc';
        }
        this.currentPage = 1;
        this.fetchYouths();
      },
      sortIcon(column) {
        if (this.sortBy !== column) return 'fa-sort opacity-5';
        return this.sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
      },
      escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (c) => ({
          '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
      },
      buildIssuesHtml(message, errors) {
        const items = (errors || [])
          .map((e) => `<li style="margin-bottom:6px;">${this.escapeHtml(e)}</li>`)
          .join('');
        return `
          <p style="margin-bottom:10px;">${this.escapeHtml(message)}</p>
          <div style="max-height:300px;overflow:auto;text-align:left;background:#f8f9fa;border-radius:8px;padding:10px 12px;">
            <ul style="padding-left:1.2rem;margin:0;">${items}</ul>
          </div>`;
      },
      async importFile() {
        this.isLoading = true;
        try {
          const formData = new FormData();
          formData.append('file', this.file);
          const { data } = await axios.post(`${PUBLIC_API_URL}/import-youth`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          const errors = data.errors || [];
          if (errors.length) {
            // El import termino, pero con incidencias: se muestran todas.
            this.$swal({
              title: data.imported ? 'Importado parcialmente' : 'No se importó',
              html: this.buildIssuesHtml(data.message, errors),
              icon: data.imported ? 'warning' : 'error',
              confirmButtonText: 'Entendido',
            });
          } else {
            this.$swal({
              title: '¡Importado con éxito!',
              text: data.message || 'Los datos del archivo fueron importados.',
              icon: 'success',
            });
          }
          this.fetchYouths();
        } catch (error) {
          // Error duro (validación de archivo, hoja faltante, fallo de lectura, red).
          const res = error.response && error.response.data ? error.response.data : {};
          const message = res.message
            || (res.errors && res.errors.file && res.errors.file[0])
            || 'No pudimos importar el archivo. Verifica que sea la plantilla correcta (.xlsx).';
          this.$swal({
            title: 'Ocurrió un error',
            html: this.buildIssuesHtml(message, res.errors && Array.isArray(res.errors) ? res.errors : []),
            icon: 'error',
            confirmButtonText: 'Entendido',
          });
        } finally {
          this.isLoading = false;
        }
      },
      async onChange(event) {
        this.file = event.target.files[0];
        if (this.file) {
          await this.importFile();
        }
        // Limpia el input (vía ref) para poder volver a elegir el mismo archivo.
        if (this.$refs.file) {
          this.$refs.file.value = '';
        }
      },
    }
  };
  </script>
  
  <style scoped>
  .table-responsive {
    overflow-x: auto;
  }
  .cursor-pointer {
    cursor: pointer;
  }
  .table.zebra tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
  .table.zebra tbody tr:hover {
    background-color: #eef2f7;
  }
  .sortable {
    cursor: pointer;
    user-select: none;
  }
  .sortable:hover {
    color: #344767 !important;
  }
  .skeleton {
    height: 14px;
    border-radius: 6px;
    background: linear-gradient(90deg, #edf0f3 25%, #f7f9fb 37%, #edf0f3 63%);
    background-size: 400% 100%;
    animation: shimmer 1.4s ease infinite;
  }
  @keyframes shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: 0 0; }
  }
  .empty-state {
    padding: 3.5rem 1.5rem;
  }
  .empty-icon {
    width: 4.5rem;
    height: 4.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(135deg, #eef2f7 0%, #e3e9f2 100%);
    color: #8392ab;
    font-size: 1.8rem;
  }
  </style>
  